import styled from '@emotion/styled';
import { Checkbox, Modal } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Button, Col, FormItem, Input, Row, Select, Text } from 'components';
import { theme } from 'configs/styled';
import { useFormik } from 'formik';
import {
  ILocalStorageSignup,
  useLocalStorageSignup,
} from 'hooks/useLocalStorageSignup';
import { useQueryParams } from 'hooks/useQueryParams';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { addressService, authenticationService } from 'services/driverPortal';
import {
  ISignUpVerifyRequest,
  OTPResponse,
} from 'services/driverPortal/AuthenticationService.interface';
import { assertThaiId } from 'utils/validateThaiCitizen';
import * as Yup from 'yup';
import { IResponseError } from '../../../utils/transformResponseError';
import {
  ASCII_DIGITS_ONLY,
  THAI_ONLY,
  TH_MOBILE_NO_SAME,
  EMAIL_FORMAT,
} from '../../../utils/validate';
import {
  getAddressProvinces,
  ISelectAddressProps,
} from '../MainPage/PersonalInformationPage';
import { InfoCircleOutlined } from '@ant-design/icons';

export const phoneRegExp = /^[0-9]{10}/;

export interface SelectTruckType {
  title: string;
  value: string;
}

const validateSignUp = Yup.object().shape({
  provinceNameArr: Yup.array().min(1, 'กรุณาเลือกจังหวัด').required(),
  districtNameArr: Yup.array()
    .min(1, 'กรุณาเลือกเขตที่พร้อมเริ่มบริการ')
    .required(),
  vehicleTypeArr: Yup.array()
    .min(1, 'กรุณาเลือกรูปแบบการขับที่สนใจ')
    .required(),
  businessTypeArr: Yup.array()
    .min(1, 'กรุณาเลือกธุรกิจที่สนใจให้บริการ')
    .required(),

  firstname: Yup.string()
    .nullable()
    .required('กรุณาระบุชื่อ')
    .matches(THAI_ONLY, 'ใส่ได้เฉพาะอักษรไทย'),
  lastname: Yup.string()
    .nullable()
    .required('กรุณาระบุนามสกุล')
    .matches(THAI_ONLY, 'ใส่ได้เฉพาะอักษรไทย'),
  citizenId: Yup.string()
    .trim()
    .required('กรุณากรอกเลขบัตรประชาชน 13 หลัก')
    .matches(ASCII_DIGITS_ONLY, 'ไม่สามารถกรอกอักษรอื่นๆได้นอกจากตัวเลข')
    .length(13, 'กรุณากรอกเลขบัตรประชาชน 13 หลัก')
    .test(
      'thai-id-checksum',
      'รูปแบบของรหัสบัตรประชาชนไม่ถูกต้อง',
      value => !!value && assertThaiId(value),
    ),
  phone: Yup.string()
    .trim()
    .required('กรุณากรอกเบอร์โทรศัพท์')
    .matches(ASCII_DIGITS_ONLY, 'ไม่สามารถกรอกอักษรอื่นๆได้นอกจากตัวเลข')
    .matches(TH_MOBILE_NO_SAME, 'กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง')
    .matches(phoneRegExp, 'กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง'),
  email: Yup.string()
    .trim()
    .required('กรุณากรอกอีเมล')
    .matches(EMAIL_FORMAT, 'กรุณากรอกอีเมลให้ถูกต้อง'),
  referrerCode: Yup.string().matches(
    ASCII_DIGITS_ONLY,
    'ไม่สามารถกรอกอักษรอื่นๆได้นอกจากตัวเลข',
  ),
  isAcceptedTerm: Yup.boolean()
    .oneOf([true], 'โปรดยอมรับข้อกำหนดในการให้บริการ')
    .required('โปรดยอมรับข้อกำหนดในการให้บริการ'),
});

export interface ISignUpForm {}

// const buildSignUpPayload = (
//   request: ISignUpVerifyRequest,
//   response: IOTPResponse,
// ) => {
//   const { otpRefCode, expiredAt, otpDigit } = response?.data;
//   return {
//     ...request,
//     otpRefCode,
//     expiredAt,
//     otpDigit,
//     byPassLogin: true,
//   } as ILocalStorageSignup;
// };

const getSubFromQuery = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const v = new URLSearchParams(window.location.search).get('sub');
  return v?.trim() ? v.trim().toUpperCase() : undefined;
};

const getReferrerFromQuery = (): string => {
  if (typeof window === 'undefined') return '';
  const v = new URLSearchParams(window.location.search).get('referrer');
  return v?.trim() || '';
};

const buildSignUpPayload = (
  request: ISignUpVerifyRequest,
  response: OTPResponse,
): ILocalStorageSignup => {
  const { otpRefcode, expired } = response;

  return {
    provinceNameArr: request.provinceNameArr || [],
    districtNameArr: request.districtNameArr || [],
    vehicleTypeArr: request.vehicleTypeArr || [],
    businessTypeArr: request.businessTypeArr || [],

    firstname: request.firstname,
    lastname: request.lastname,
    citizenId: request.citizenId,
    phone: request.phone,
    referrerCode: request.referrerCode,
    isAcceptedTerm: request.isAcceptedTerm,
    vendorName: request.vendorName,

    otpRefCode: otpRefcode,
    expired,
    byPassLogin: true,
    otpDigit: 6,
  };
};

const ModalStyle = styled(Modal)`
  .ant-modal-content {
    font-family: ${theme.fonts.NotoSansTh}, ${theme.fonts.NotoSands};
  }
`;

const SmallTagSelectWrapper = styled.div`
  .signup-multi-select .ant-select-selector {
    height: 40px !important;
    min-height: 40px !important;
    overflow: hidden !important;
    align-items: center !important;
  }

  .signup-multi-select .ant-select-selection-overflow {
    flex-wrap: nowrap !important;
    overflow: hidden !important;
    height: 100%;
    align-items: center;
  }

  .signup-multi-select .ant-select-selection-overflow-item {
    flex: none !important;
  }

  .signup-multi-select .ant-select-selection-item {
    margin-top: 0px !important;
    margin-bottom: 0 !important;
    height: 24px !important;
    line-height: 32px !important;
    align-items: center;
  }
  .signup-multi-select .ant-select-selection-item-content,
  .signup-multi-select .custom-plus-tag {
    max-width: none !important;
    width: auto !important;
    display: inline-flex !important;
    overflow: visible !important;
    text-overflow: clip !important;
    white-space: nowrap !important;
    font-size: 13px !important;
  }
`;

const SignUpForm: React.FC<ISignUpForm> = props => {
  const { search, pushUrl } = useQueryParams();
  const [isDisableSubmit, setIsDisableSubmit] = useState(true);
  const [provinces, setProvinces] = useState<ISelectAddressProps[]>([]);
  const [districts, setDistricts] = useState<ISelectAddressProps[]>([]);
  const [businessTypes, setBusinessTypes] = useState<ISelectAddressProps[]>([]);
  const [truckType, setTruckType] = useState<ISelectAddressProps[]>([]);
  const [interestedCompany, setInterestedCompany] = useState<
    ISelectAddressProps[]
  >([]);

  const [loadingPage, setLoadingPage] = useState(true);
  const [serviceAreas, setServiceAreas] = useState<ISelectAddressProps[]>([]);
  const localStorageSignup = useLocalStorageSignup();
  const [otpLoading, setOtpLoading] = React.useState(false);
  const [subcontractorName, setSubcontractorName] = useState('All Now');
  const [
    showInvalidSubContractorModal,
    setShowInvalidSubContractorModal,
  ] = useState(false);
  const history = useHistory();

  const gotoOtp = () => {
    pushUrl('/otp', '');
  };

  const formik = useFormik<ISignUpVerifyRequest>({
    initialValues: {
      provinceNameArr: [],
      districtNameArr: [],
      vehicleTypeArr: [],
      businessTypeArr: [],

      firstname: '',
      lastname: '',
      citizenId: '',
      phone: '',
      email: '',
      referrerCode: '',
      isAcceptedTerm: false,
      vendorName: '',
    },
    validationSchema: validateSignUp,
    onSubmit: async formValues => {
      if (otpLoading) return;
      setOtpLoading(true);
      try {
        const sub = getSubFromQuery() || 'ANL';
        const referrer = getReferrerFromQuery();
        localStorage.setItem('sub', sub);

        const provinceNames = provinces
          .filter(p => formValues.provinceNameArr?.includes(p.value)) // value = id
          .map(p => p.title);

        const newModel = {
          province_id: provinceNames,
          district_id: formValues.districtNameArr,
          rider_type: formValues.vehicleTypeArr,
          interested_company_id: formValues.businessTypeArr,

          first_name_th: formValues.firstname,
          last_name_th: formValues.lastname,
          citizen_id: formValues.citizenId,
          tel: formValues.phone,
          email: formValues.email,
          referCode: formValues.referrerCode,
          referrer_id: referrer,
          isAcceptedTerm: formValues.isAcceptedTerm,
          vendorName: sub,
        };

        const response = await authenticationService.signupVerify(
          newModel,
          sub,
        );

        const signup = buildSignUpPayload(
          {
            ...formValues,
            provinceNameArr: provinceNames,
          },
          response,
        );

        localStorageSignup.setValues(signup);
        gotoOtp();
      } catch (error) {
        const _error = error as IResponseError;

        const msg =
          String(_error?.data?.error || '').trim() ||
          'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';

        Modal.error({
          title: 'เกิดข้อผิดพลาด',
          content: msg,
          okText: 'ตกลง',
        });
      } finally {
        setOtpLoading(false);
      }
    },
  });

  const verifyVendor = async (subcontractorQueryParam: string) => {
    const response = await authenticationService.vendorVerify({
      name: subcontractorQueryParam,
    });
    if (response?.status === 'success') {
      setSubcontractorName(subcontractorQueryParam);
    } else {
      setShowInvalidSubContractorModal(true);
    }
  };

  const getLmTruckTypes = async () => {
    try {
      return await addressService.LmRiderTruckTypes();
    } catch (err) {}
  };

  const getInterested = async () => {
    try {
      return await addressService.Interested();
    } catch (err) {}
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const subcontractorQueryParam = searchParams.get('subcontractorName');
    if (subcontractorQueryParam) {
      verifyVendor(subcontractorQueryParam);
    }
  }, []);

  const getAddressDistricts = async (provinceIds: string[]) => {
    const ids = provinceIds.filter(Boolean);

    console.log('send province_id:', ids.join(','));

    if (!ids.length) return null;

    try {
      return await addressService.districts({
        province_id: ids.join(','),
      });
    } catch (err) {
      return null;
    }
  };

  const fetchDistricts = async (provinceIds: string[]) => {
    if (!provinceIds?.length) {
      setDistricts([]);
      formik.setFieldValue('districtNameArr', []);
      return;
    }

    const response = await getAddressDistricts(provinceIds);
    if (!response) {
      setDistricts([]);
      return;
    }

    const provinceMap = Object.fromEntries(
      provinces.map(p => [String(p._id), p.title]),
    );

    const groupedOptions = provinceIds.flatMap(provinceId => {
      const provinceName = provinceMap[String(provinceId)];

      const districtOptions = response.dataList
        .filter(d => String(d.province_id) === String(provinceId))
        .map(district => ({
          _id: district.id.toString(),
          title: district.district_name_th,
          value: district.district_name_th,
        }));

      return [
        {
          _id: `province-${provinceId}`,
          title: provinceName,
          value: `province-${provinceId}`,
          disabled: true,
        },
        ...districtOptions,
      ];
    });

    setDistricts(groupedOptions);
  };

  useEffect(() => {
    const getProvinces = async () => {
      const response = await getAddressProvinces();
      const provinces = response;

      if (!provinces) return;

      const _provinces = provinces.dataList.map(province => {
        return {
          _id: province.id.toString(),
          title: province.province_name_th,
          value: province.id.toString(),
        };
      });

      setProvinces(_provinces);
      setLoadingPage(false);
    };
    getProvinces();

    const getRiderType = async () => {
      const truckType = await getLmTruckTypes();
      if (!truckType) return;

      const _truckType = truckType.dataList.map(trucktypes => {
        return {
          _id: trucktypes.id.toString(),
          value: trucktypes.id.toString(),
          title: trucktypes.name,
        };
      });

      setTruckType(_truckType);
      setLoadingPage(false);
    };

    getRiderType();

    const getInterestedCompany = async () => {
      const interestedCompany = await getInterested();
      if (!interestedCompany) return;

      const _InterestedCompany = interestedCompany.dataList.map(
        interestedCompanys => {
          return {
            _id: interestedCompanys.id.toString(),
            value: interestedCompanys.id.toString(),
            title: interestedCompanys.name,
          };
        },
      );

      setInterestedCompany(_InterestedCompany);
      setLoadingPage(false);
    };

    getInterestedCompany();

    const getTruckTypes = async () => {
      const truckType = await getLmTruckTypes();
      if (!truckType) return;

      const _truckType = truckType.dataList.map(trucktypes => {
        return {
          _id: trucktypes.id.toString(),
          value: trucktypes.id.toString(),
          title: trucktypes.name,
        };
      });

      setTruckType(_truckType);
      setLoadingPage(false);
    };

    getTruckTypes();
  }, []);

  useEffect(() => {
    const { isAcceptedTerm } = formik.values;
    if (isAcceptedTerm) {
      setIsDisableSubmit(false);
    }

    if (!isAcceptedTerm) {
      setIsDisableSubmit(true);
    }
  }, [formik.values]);

  const errors = formik.errors || {};

  const handleSelectVehicle = (vehicleSelected: string) => {
    formik.setFieldValue('vehicleType', vehicleSelected);
  };

  const handleCheckTermOfService = (e: CheckboxChangeEvent) => {
    formik.setFieldValue('isAcceptedTerm', e.target.checked);
  };

  const shouldDisplayErrorMessage = (key: keyof ISignUpVerifyRequest) => {
    return formik.touched[key] && formik.errors[key];
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(e.target.name, e.target.value.trim());
  };

  const baseURLVMP = process.env.REACT_APP_VMP_URL || '';

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormItem
        label="จังหวัดที่พร้อมเริ่มให้บริการ (เลือกได้หลายจังหวัด)"
        required
        labelCol={{ span: 24 }}
        colon={false}
        validateStatus={
          formik.touched.provinceNameArr && formik.errors.provinceNameArr
            ? 'error'
            : ''
        }
        help={
          formik.touched.provinceNameArr
            ? (formik.errors.provinceNameArr as string)
            : undefined
        }
      >
        <SmallTagSelectWrapper>
          <Select
            mode="multiple"
            size="large"
            className="signup-multi-select"
            showSearch
            maxTagCount={3}
            maxTagPlaceholder={omittedValues => (
              <span className="custom-plus-tag">+{omittedValues.length}</span>
            )}
            placeholder="เลือกจังหวัดที่พร้อมเริ่มให้บริการ"
            optionFilterProp="children"
            options={provinces}
            value={formik.values.provinceNameArr}
            onChange={provinceIds => {
              formik.setFieldTouched('provinceNameArr', true, false);
              formik.setFieldValue('provinceNameArr', provinceIds, false);
              formik.setFieldValue('districtNameArr', [], false);

              if (!provinceIds?.length) {
                setDistricts([]);
              }

              setTimeout(() => {
                formik.validateForm();
              }, 0);
            }}
            onBlur={async () => {
              formik.setFieldTouched('provinceNameArr', true, true);

              const provinceIds = formik.values.provinceNameArr || [];

              if (provinceIds.length) {
                await fetchDistricts(provinceIds);
              }
            }}
          />
        </SmallTagSelectWrapper>
      </FormItem>
      <FormItem
        label="เขตที่พร้อมเริ่มบริการ (เลือกได้หลายเขต)"
        required
        labelCol={{ span: 24 }}
        colon={false}
        validateStatus={
          formik.touched.districtNameArr && formik.errors.districtNameArr
            ? 'error'
            : ''
        }
        help={
          formik.touched.districtNameArr
            ? (formik.errors.districtNameArr as string)
            : undefined
        }
      >
        <SmallTagSelectWrapper>
          <Select
            mode="multiple"
            size="large"
            showSearch
            className="signup-multi-select"
            maxTagCount={3}
            maxTagPlaceholder={omittedValues => (
              <span className="custom-plus-tag">+{omittedValues.length}</span>
            )}
            placeholder="เลือกเขตที่พร้อมเริ่มบริการ"
            optionFilterProp="children"
            options={districts}
            value={formik.values.districtNameArr}
            disabled={!formik.values.provinceNameArr?.length}
            onChange={val => formik.setFieldValue('districtNameArr', val, true)}
            onBlur={() => formik.setFieldTouched('districtNameArr', true)}
          />
        </SmallTagSelectWrapper>
      </FormItem>
      <FormItem
        label="รูปแบบการขับที่สนใจ (เลือกได้หลายรูปแบบ)"
        required
        labelCol={{ span: 24 }}
        colon={false}
        validateStatus={
          formik.touched.vehicleTypeArr && formik.errors.vehicleTypeArr
            ? 'error'
            : ''
        }
        help={
          formik.touched.vehicleTypeArr
            ? (formik.errors.vehicleTypeArr as string)
            : undefined
        }
      >
        <SmallTagSelectWrapper>
          <Select
            mode="multiple"
            size="large"
            showSearch
            className="signup-multi-select"
            maxTagCount={1}
            maxTagPlaceholder={omittedValues => (
              <span className="custom-plus-tag">+{omittedValues.length}</span>
            )}
            placeholder="เลือกรูปแบบการขับที่สนใจ"
            optionFilterProp="children"
            options={truckType}
            value={formik.values.vehicleTypeArr}
            onChange={val => formik.setFieldValue('vehicleTypeArr', val, true)}
            onBlur={() => formik.setFieldTouched('vehicleTypeArr', true)}
          />
        </SmallTagSelectWrapper>
      </FormItem>
      <FormItem
        label="ธุรกิจที่สนใจให้บริการ (เลือกได้หลายธุรกิจ)"
        required
        labelCol={{ span: 24 }}
        colon={false}
        validateStatus={
          formik.touched.businessTypeArr && formik.errors.businessTypeArr
            ? 'error'
            : ''
        }
        help={
          formik.touched.businessTypeArr
            ? (formik.errors.businessTypeArr as string)
            : undefined
        }
      >
        <SmallTagSelectWrapper>
          <Select
            mode="multiple"
            size="large"
            showSearch
            className="signup-multi-select"
            maxTagCount={3}
            maxTagPlaceholder={omittedValues => (
              <span className="custom-plus-tag">+{omittedValues.length}</span>
            )}
            placeholder="เลือกธุรกิจที่สนใจให้บริการ"
            optionFilterProp="children"
            options={
              (getSubFromQuery() || 'ANL') === 'ANL'
                ? interestedCompany
                : interestedCompany.filter(c => c._id === '1')
            }
            value={formik.values.businessTypeArr}
            onChange={val => formik.setFieldValue('businessTypeArr', val, true)}
            onBlur={() => formik.setFieldTouched('businessTypeArr', true)}
          />
        </SmallTagSelectWrapper>
      </FormItem>
      {/* {subcontractorName === 'All Now' && ( */}
      {/*<FormItem*/}
      {/*  label="ประเภทรถ"*/}
      {/*  required*/}
      {/*  labelCol={{ span: 24 }}*/}
      {/*  colon={false}*/}
      {/*  validateStatus={*/}
      {/*    formik.touched.vehicleType && formik.errors.vehicleType ? 'error' : ''*/}
      {/*  }*/}
      {/*  help={*/}
      {/*    formik.touched.vehicleType*/}
      {/*      ? (formik.errors.vehicleType as string)*/}
      {/*      : undefined*/}
      {/*  }*/}
      {/*>*/}
      {/*  <Select*/}
      {/*    size="large"*/}
      {/*    showSearch*/}
      {/*    placeholder="เลือกประเภทรถ"*/}
      {/*    optionFilterProp="children"*/}
      {/*    options={truckType}*/}
      {/*    onChange={val => {*/}
      {/*      formik.setFieldValue('vehicleType', val, true);*/}
      {/*    }}*/}
      {/*    onBlur={() => formik.setFieldTouched('vehicleType', true)}*/}
      {/*    value={formik.values.vehicleType}*/}
      {/*  />*/}
      {/*</FormItem>*/}
      {/* )} */}
      <Row gutter={24}>
        <Col span={12}>
          <FormItem
            label="ชื่อ (ภาษาไทย)"
            required
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={
              shouldDisplayErrorMessage('firstname') ? 'error' : ''
            }
            help={shouldDisplayErrorMessage('firstname') && errors.firstname}
          >
            <Input
              size="large"
              name="firstname"
              placeholder="กรอกชื่อ"
              onChange={handleChangeInput}
              value={formik.values.firstname}
              onBlur={formik.handleBlur}
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem
            label="นามสกุล (ภาษาไทย)"
            required
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={
              shouldDisplayErrorMessage('lastname') ? 'error' : ''
            }
            help={shouldDisplayErrorMessage('lastname') && errors.lastname}
          >
            <Input
              size="large"
              name="lastname"
              placeholder="กรอกนามสกุล"
              onChange={handleChangeInput}
              onBlur={formik.handleBlur}
              value={formik.values.lastname}
            />
          </FormItem>
        </Col>
      </Row>
      <FormItem
        label="เลขบัตรประชาชน 13 หลัก"
        required
        labelCol={{ span: 24 }}
        colon={false}
        validateStatus={shouldDisplayErrorMessage('citizenId') ? 'error' : ''}
        help={shouldDisplayErrorMessage('citizenId') && errors.citizenId}
      >
        <Input
          size="large"
          name="citizenId"
          placeholder="ตัวอย่าง 3500000000000"
          onChange={e =>
            formik.setFieldValue(
              'citizenId',
              e.target.value.replace(/[^0-9]/g, '').slice(0, 13),
            )
          }
          onBlur={formik.handleBlur}
          value={formik.values.citizenId}
          maxLength={13}
          inputMode="numeric"
          pattern="[0-9]*"
        />
      </FormItem>
      <FormItem
        label="เบอร์โทรศัพท์ (ใช้เบอร์เดียวกับที่ลงทะเบียน TrueMoney)"
        required
        labelCol={{ span: 24 }}
        colon={false}
        validateStatus={shouldDisplayErrorMessage('phone') ? 'error' : ''}
        help={shouldDisplayErrorMessage('phone') && errors.phone}
      >
        <Input
          size="large"
          name="phone"
          placeholder="ตัวอย่าง 0800000000"
          value={formik.values.phone ?? ''}
          onChange={e =>
            formik.setFieldValue(
              'phone',
              e.target.value.replace(/[^0-9]/g, '').slice(0, 10),
            )
          }
          onBlur={formik.handleBlur}
          maxLength={10}
          inputMode="tel"
          pattern="[0-9]*"
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            marginTop: 10,
            color: '#595959',
            fontSize: 12,
          }}
        >
          <InfoCircleOutlined
            style={{
              color: '#1677ff',
              fontSize: 13,
            }}
          />
          <span>
            กรณียังไม่เคยลงทะเบียน TrueMoney สามารถลงทะเบียนภายหลังได้
          </span>
        </div>
      </FormItem>
      <FormItem
        label="อีเมล"
        required
        labelCol={{ span: 24 }}
        colon={false}
        validateStatus={shouldDisplayErrorMessage('email') ? 'error' : ''}
        help={shouldDisplayErrorMessage('email') && errors.email}
      >
        <Input
          size="large"
          name="email"
          placeholder="ตัวอย่าง example@email.com"
          value={formik.values.email ?? ''}
          onChange={e => formik.setFieldValue('email', e.target.value.trim())}
          onBlur={formik.handleBlur}
          maxLength={100}
          inputMode="email"
        />
      </FormItem>
      <FormItem
        label={
          <div>
            <span>รหัสแนะนำคนขับ</span>
            <p
              style={{
                fontSize: theme.fontSize.xxs,
                color: theme.palettes.black40,
              }}
            >
              รหัสของผู้แนะนำคุณ (8 หลัก)
            </p>
          </div>
        }
        labelCol={{ span: 24 }}
        colon={false}
        validateStatus={
          shouldDisplayErrorMessage('referrerCode') ? 'error' : ''
        }
        help={shouldDisplayErrorMessage('referrerCode') && errors.referrerCode}
      >
        <Input
          size="large"
          name="referrerCode"
          placeholder="ตัวอย่าง 22233328"
          value={formik.values.referrerCode ?? ''}
          onChange={e =>
            formik.setFieldValue(
              'referrerCode',
              e.target.value.replace(/[^0-9]/g, '').slice(0, 8),
            )
          }
          onBlur={formik.handleBlur}
          maxLength={8}
          inputMode="numeric"
          pattern="[0-9]*"
        />
      </FormItem>
      <FormItem>
        <Row gutter={[24, 12]}>
          <Col span={1}>
            <Checkbox onChange={handleCheckTermOfService} />
          </Col>
          <Col span={22}>
            <span
              style={{
                fontSize: theme.fontSize.xxs,
              }}
            >
              ข้าพเจ้าได้อ่านและยอมรับ
              <a
                href={`${baseURLVMP}/term/T_C_2W_All_Now_Logistics.pdf`}
                target="_blank"
                style={{ marginLeft: '3px' }}
              >
                ข้อกำหนดและเงื่อนไขการเป็นผู้ให้บริการขนส่งรถจักรยานยนต์กับ
                บริษัท ออลล์ นาว โลจิสติกส์
              </a>
              &nbsp;และกับ&nbsp;
              <a
                href={`${baseURLVMP}/term/T_C_2W_All_Now_Management.pdf`}
                target="_blank"
                style={{ marginLeft: '3px' }}
              >
                บริษัท ออลล์ นาว แมนเนจเม้นท์
              </a>
              &nbsp;รวมถึง&nbsp;
              <a
                href="https://www.allnowgroup.com/policy"
                target="_blank"
                style={{ marginLeft: '3px' }}
              >
                ประกาศเกี่ยวกับการคุ้มครองข้อมูลส่วนบุคคล
              </a>
              {/*&nbsp;และ*/}
              {/*<Link*/}
              {/*  to={'/driver/terms-condition'}*/}
              {/*  target="_blank"*/}
              {/*  style={{ marginLeft: '3px' }}*/}
              {/*>*/}
              {/*  ข้อตกลงและเงื่อนไขการใช้งานเว็บไซต์*/}
              {/*</Link>*/}
            </span>
          </Col>
        </Row>
      </FormItem>
      <Button
        block
        htmlType="submit"
        disabled={isDisableSubmit}
        loading={otpLoading}
        type={isDisableSubmit ? 'default' : 'primary'}
        size="large"
      >
        สมัครสมาชิก
      </Button>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          paddingTop: '10px',
        }}
      >
        <Text>มีบัญชีแล้ว?</Text>
        <Link
          style={{ paddingLeft: '5px' }}
          to={`/login${history?.location?.search}`}
        >
          เข้าสู่ระบบ
        </Link>
      </div>
      <ModalStyle
        title={
          <span style={{ color: theme.palettes.red }}>เกิดข้อผิดพลาด</span>
        }
        visible={showInvalidSubContractorModal}
        closable={false}
        footer={null}
        centered
      >
        URL ที่ใช้สมัครไม่ถูกต้อง หรือไม่พบข้อมูลของ Subcontractor ในระบบ
        กรุณาตรวจสอบข้อมูลที่ป้อนหรือเพิ่มข้อมูล Subcontractor ใหม่
      </ModalStyle>
    </form>
  );
};

export default SignUpForm;
